import classnames from 'classnames';
import dynamic from 'next/dynamic';
import React from 'react';

import type { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import type { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import type { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import type { PurchaseReasonEnum } from '@cineamo/api/constants/PurchaseReason.enum';

import type { UrlQueryParameter } from '@cineamo/api/helpers/url-query-helper';

import { useCinemasShowingsSearch } from '../hooks/useCinemasShowingsSearch';
import { LoadingLayout } from '../layouts/LoadingLayout';
import useLocationStore from '../stores/location/useLocationStore';

import CinemaRow from './CinemaRow';
import CinemasListSkeleton from './CinemasListSkeleton';
import NoShowingsFound from './NoShowingsFound';
import PreReleaseLayout from './PreReleaseLayout';
import ViewTypeSelector from './ViewTypeSelector';

// Dynamically import MapView to avoid SSR issues with Leaflet
const DynamicMapView = dynamic(() => import('./MapView'), {
    ssr: false,
    loading: () => (
        <div className="w-full h-full flex items-center justify-center bg-[var(--background-color)]">
            <div className="text-body-14 opacity-70">Loading map...</div>
        </div>
    )
});

const ShowingsSearchResults = ({
    selectedDate,
    viewType,
    setViewType,
    movieId,
    movieReleaseDate,
    renderCinemaAction,
    renderTicketButton,
    purchaseReason,
    apiFilters,
    showShowingProperties
}: {
    selectedDate: string;
    viewType: 'list' | 'map';
    setViewType: (viewType: 'list' | 'map') => void;
    movieId: MovieDto['id'];
    movieReleaseDate?: string;
    renderCinemaAction?: (cinema: CinemaDto) => React.ReactNode;
    renderTicketButton?: (showing: ShowingDto, cinema: CinemaDto) => React.ReactNode;
    purchaseReason?: PurchaseReasonEnum;
    apiFilters?: UrlQueryParameter;
    showShowingProperties?: boolean;
}) => {
    const { isAllLocationDetailsAvailable } = useLocationStore();
    // `releaseDate` bounds the PREVIEWS view (showings strictly before the release date);
    // it is ignored for every other selectedDate.
    const { cinemasWithShowings, hasNextPage, isLoading, isFetchingNextPage, loaderRef } = useCinemasShowingsSearch(
        movieId,
        selectedDate,
        apiFilters,
        { releaseDate: movieReleaseDate }
    );

    const isMovieReleased = movieReleaseDate && new Date(movieReleaseDate) < new Date();

    // Check if location is available - prevents hydration mismatch
    const hasLocation = isAllLocationDetailsAvailable();

    return (
        <div className="bg-[var(--widget-background-color)] grow min-h-0 flex flex-col">
            <ViewTypeSelector
                viewType={viewType}
                setViewType={setViewType}
                className="bg-[var(--surface-color)]"
                selectedItemClassName="bg-[var(--background-color)]"
                notSelectedItemClassName="bg-transparent"
            />
            <div className="flex-1 min-h-0">
                <LoadingLayout
                    isLoading={!hasLocation || isLoading}
                    loadingComponent={<CinemasListSkeleton />}
                    isNoResults={cinemasWithShowings.length === 0}
                    noResultsComponent={
                        isMovieReleased ? <NoShowingsFound /> : <PreReleaseLayout releaseDate={movieReleaseDate} />
                    }
                    resultComponent={
                        <div className="h-full flex flex-col lg:flex-row lg:overflow-hidden">
                            {/* Map View - On top for mobile, right side on desktop */}
                            <div
                                className={classnames(
                                    'transition-all duration-300 order-first lg:order-last flex-shrink-0 lg:p-12',
                                    // Mobile: show as overlay when map view selected
                                    viewType === 'map' ? 'h-[300px]' : 'h-0',
                                    // Desktop: collapse width to 0 when hidden
                                    viewType === 'map' ? 'lg:w-1/2 lg:h-full' : 'lg:w-0 lg:h-full'
                                )}>
                                <div className="w-full h-full lg:rounded-8 overflow-hidden">
                                    <DynamicMapView
                                        cinemas={cinemasWithShowings}
                                        selectedDate={selectedDate}
                                        renderCinemaAction={renderCinemaAction}
                                        renderTicketButton={renderTicketButton}
                                        purchaseReason={purchaseReason}
                                        showShowingProperties={showShowingProperties}
                                    />
                                </div>
                            </div>

                            {/* Cinema List - Below map on mobile, left side on desktop */}
                            <div
                                className={classnames(
                                    'flex flex-col space-y-12 px-12 py-12 overflow-y-auto order-last lg:order-first transition-all duration-300',
                                    viewType === 'map' ? 'lg:w-1/2' : 'lg:w-full'
                                )}>
                                {cinemasWithShowings?.map((cinema) => (
                                    <CinemaRow
                                        key={`CinemaRow-${cinema.id}`}
                                        viewType={viewType}
                                        cinema={cinema}
                                        selectedDate={selectedDate}
                                        renderCinemaAction={renderCinemaAction}
                                        renderTicketButton={renderTicketButton}
                                        purchaseReason={purchaseReason}
                                        showShowingProperties={showShowingProperties}
                                    />
                                ))}

                                <div className="flex flex-row">
                                    <div
                                        className="loader-element"
                                        ref={loaderRef}
                                    />

                                    {hasNextPage !== false && isFetchingNextPage && <CinemasListSkeleton />}
                                </div>
                            </div>
                        </div>
                    }
                />
            </div>
        </div>
    );
};
export default ShowingsSearchResults;
